import { useState } from "react";
import { Mail, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COMPANY_EMAIL = "aivvvora@gmail.com";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.subject.trim()) e.subject = "Subject is required.";
    if (!form.message.trim()) e.message = "Please write a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent. We'll be in touch!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] vora-hero-radial">
      <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
      <section className="relative mx-auto max-w-5xl px-5 sm:px-8 py-24">
        <Reveal>
          <SectionLabel>Contact</SectionLabel>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">Get in touch.</h1>
          <a href={`mailto:${COMPANY_EMAIL}`} className="mt-4 inline-flex items-center gap-2 text-base text-slate-300 hover:text-white transition-colors" data-testid="contact-email">
            <Mail className="h-4 w-4 text-[#4F8CFF]" /> {COMPANY_EMAIL}
          </a>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 vora-glass rounded-3xl p-6 sm:p-9 max-w-2xl">
            {done ? (
              <div className="text-center py-10" data-testid="contact-success">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-400"><CheckCircle2 className="h-8 w-8" /></div>
                <h2 className="mt-6 text-2xl font-semibold text-white">Message sent</h2>
                <p className="mt-2 text-slate-400">Thanks for reaching out — we'll reply to {form.email} soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form" noValidate>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Name</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="contact-name" />
                    {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="contact-email-input" />
                    {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Subject</Label>
                  <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="contact-subject" />
                  {errors.subject && <p className="mt-1 text-xs text-red-400">{errors.subject}</p>}
                </div>
                <div>
                  <Label className="text-slate-300">Message</Label>
                  <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF] min-h-[120px]" data-testid="contact-message" />
                  {errors.message && <p className="mt-1 text-xs text-red-400">{errors.message}</p>}
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold" data-testid="contact-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageSquare className="mr-2 h-4 w-4" /> Submit</>}
                </Button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
